import * as React from "react";
import { formSet, useCpFormAnyValue, useCpFormAnyValueId, useCpFormValue } from "../../gvc/testNewForm/utils";
import { BasePathContext, useBasePath } from "../../../reactContexts/basePathContext";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { ContentLayout } from "../../../components/layout/contentLayout";
import { SingleNavMenuLink, toLinkButton, toLinkPage, toLinkSpace } from "../../identity/useNavMenu";
import { useAppDispatch } from "../../../store/hooks";
import { setRedirect } from "../../../store/contextSlice";
import { IS_DEPLOYMENT_ENV_PROD, IS_LOCAL } from "../../../envVariables";
import { SingleNavMenu } from "../../../components/antd/layout/singleNavMenu";
import { useCpFormContext } from "../../../components/form/useCpFormContext";
import { CpTagsNew } from "../../../components/form/CpTagsNew";
import { General } from "./general";
import { Provider } from "./provider/provider";
import { Addons } from "./addons/addons";
import { Firewall } from "./firewall";
import { NodePools } from "./provider/nodePools";
import { UnmanagedNodePools } from "./provider/unmanagedNodePools";
import { providerLabelMap } from "../../../newcomponents/table/columns/wellKnown/providerColumn";
import { AddonByok } from "./addons/byok";
import { AddonAwsEcr } from "./addons/awsEcr";
import { AddonAwsEfs } from "./addons/awsEfs";
import { AddonAwsElb } from "./addons/awsElb";
import { AddonAzureAcr } from "./addons/azureAcr";
import { AddonAzureWorkloadIdentity } from "./addons/azureWorkloadIdentity";
import { AddonLogs } from "./addons/logs";
import { AddonMetrics } from "./addons/metrics";
import { AddonNvidia } from "./addons/nvidia";
import { AddonRegistryMirror } from "./addons/registryMirror";
import { CpRootError } from "../../gvc/testNewForm/CpRootError";

interface Props {
  ignoreBlockerRef: React.RefObject<boolean>;
}
export const Mk8sCreateContent: React.FC<Props> = ({ ignoreBlockerRef }) => {
  const dispatch = useAppDispatch();
  const basePath = useBasePath(`/mk8s/-create/*`);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { id: formId, submit } = useCpFormContext();

  const submitting = useCpFormAnyValue(`meta.submitting`);
  const asyncValidating = useCpFormAnyValue(`meta.asyncValidating`);
  const provider = useCpFormValue(`provider`);

  const isAddonCplnPlatformEnabled = useCpFormValue(`addons.byok.isEnabled`);
  const isAddonAWSECREnabled = useCpFormValue(`addons.awsECR.isEnabled`);
  const isAddonAWSEFSEnabled = useCpFormValue(`addons.awsEFS.isEnabled`);
  const isAddonAWSELBEnabled = useCpFormValue(`addons.awsELB.isEnabled`);
  const isAddonACREnabled = useCpFormValue(`addons.azureACR.isEnabled`);
  const isAddonAWIEnabled = useCpFormValue(`addons.azureWorkloadIdentity.isEnabled`);
  const isAddonLogsEnabled = useCpFormValue(`addons.logs.isEnabled`);
  const isAddonMetricsEnabled = useCpFormValue(`addons.metrics.isEnabled`);
  const isAddonNvidiaEnabled = useCpFormValue(`addons.nvidia.isEnabled`);
  const isAddonRegistryMirrorEnabled = useCpFormValue(`addons.registryMirror.isEnabled`);

  React.useEffect(() => {
    const startingPage = `${basePath}/general`;
    navigate(startingPage, { replace: true });
  }, []);

  const links = React.useMemo(() => {
    const links: SingleNavMenuLink[] = [];

    let providerNames: string[] = [];
    switch (provider) {
      case "aws":
        providerNames = [
          `aws.autoscaler`,
          `aws.awsTags`,
          `aws.deployRoleArn`,
          `aws.deployRoleChain`,
          `aws.diskEncryptionKeyArn`,
          `aws.extraNodePolicies`,
          `aws.image`,
          `aws.keyPair`,
          `aws.networking`,
          `aws.preInstallScript`,
          `aws.region`,
          `aws.securityGroupIds`,
          `aws.skipCreateRoles`,
          `aws.vpcId`,
        ];
        break;
      case "azure":
        providerNames = [
          `azure.location`,
          `azure.subscriptionId`,
          `azure.sdkSecretLink`,
          `azure.resourceGroup`,
          `azure.networking`,
          `azure.preInstallScript`,
          `azure.image`,
          `azure.sshKeys`,
          `azure.networkId`,
          `azure.tags`,
          `azure.autoscaler`,
        ];
        break;
      case "digitalocean":
        providerNames = [
          `digitalocean.region`,
          `digitalocean.digitalOceanTags`,
          `digitalocean.tokenSecretLink`,
          `digitalocean.vpcId`,
          `digitalocean.image`,
          `digitalocean.sshKeys`,
          `digitalocean.extraSshKeys`,
          `digitalocean.reservedIPs`,
          `digitalocean.autoscaler`,
          `digitalocean.networking`,
          `digitalocean.preInstallScript`,
        ];
        break;
      case "ephemeral":
        providerNames = [`ephemeral.location`];
        break;
      case "gcp":
        providerNames = [
          `gcp.projectId`,
          `gcp.region`,
          `gcp.labels`,
          `gcp.tags`,
          `gcp.metadata`,
          `gcp.network`,
          `gcp.saKeyLink`,
          `gcp.image`,
          `gcp.preInstallScript`,
          `gcp.autoscaler`,
          `gcp.networking`,
        ];
        break;
      case "generic":
        providerNames = [`generic.location`, `generic.networking`];
        break;
      case "hetzner":
        providerNames = [
          `hetzner.region`,
          `hetzner.hetznerLabels`,
          `hetzner.networking`,
          `hetzner.preInstallScript`,
          `hetzner.tokenSecretLink`,
          `hetzner.networkId`,
          `hetzner.firewallId`,
          `hetzner.image`,
          `hetzner.sshKey`,
          `hetzner.autoscaler`,
          `hetzner.floatingIPSelector`,
        ];
        break;
      case "lambdalabs":
        providerNames = [
          `lambdalabs.region`,
          `lambdalabs.tokenSecretLink`,
          `lambdalabs.sshKey`,
          `lambdalabs.preInstallScript`,
          `lambdalabs.autoscaler`,
          `lambdalabs.fileSystems`,
        ];
        break;
      case "linode":
        providerNames = [
          `linode.region`,
          `linode.tokenSecretLink`,
          `linode.firewallId`,
          `linode.image`,
          `linode.authorizedUsers`,
          `linode.authorizedKeys`,
          `linode.vpcId`,
          `linode.networking`,
          `linode.preInstallScript`,
          `linode.autoscaler`,
        ];
        break;
      case "oblivus":
        providerNames = [
          `oblivus.datacenter`,
          `oblivus.tokenSecretLink`,
          `oblivus.sshKeys`,
          `oblivus.autoscaler`,
          `oblivus.preInstallScript`,
        ];
        break;
      case "paperspace":
        providerNames = [
          `paperspace.region`,
          `paperspace.tokenSecretLink`,
          `paperspace.sharedDrives`,
          `paperspace.autoscaler`,
          `paperspace.preInstallScript`,
          `paperspace.userIds`,
          `paperspace.networkId`,
        ];
        break;
      case "triton":
        providerNames = [
          `triton.connection`,
          `triton.location`,
          `triton.privateNetworkId`,
          `triton.firewallEnabled`,
          `triton.loadBalancer`,
          `triton.imageId`,
          `triton.sshKeys`,
          `triton.autoscaler`,
          `triton.networking`,
          `triton.preInstallScript`,
        ];
        break;
      default:
        break;
    }

    links.push(
      //
      toLinkPage("Cluster", "/general", { names: ["name", "description"] }),
      toLinkPage(`Provider | ${providerLabelMap[provider]}`, `/provider-${provider}`, {
        names: providerNames,
      }),
      toLinkPage("Node Pools", `/node-pools-${provider}`, { names: [`${provider}.nodePools`] }),
    );
    if (["hetzner"].includes(provider)) {
      links.push(
        toLinkPage("Dedicated Node Pools", `/dedicated-server-node-pools-${provider}`, {
          names: [`${provider}.dedicatedServerNodePools`],
        }),
      );
    }
    if (["lambdalabs", "oblivus", "paperspace"].includes(provider)) {
      links.push(
        toLinkPage("Unmanaged Node Pools", `/unmanaged-node-pools-${provider}`, {
          names: [`${provider}.unmanagedNodePools`],
        }),
      );
    }
    links.push(toLinkPage("Add-Ons", `/addons`, { names: ["addons"] }));
    if (isAddonCplnPlatformEnabled) {
      links.push(toLinkPage("CPLN Platform", `/addons/addon-cpln-platform`, { names: ["addons.byok"], subLevel: 1 }));
    }
    if (isAddonAWSECREnabled) {
      links.push(toLinkPage("AWS ECR", `/addons/addon-aws-ecr`, { names: ["addons.awsECR"], subLevel: 1 }));
    }
    if (isAddonAWSEFSEnabled) {
      links.push(toLinkPage("AWS EFS", `/addons/addon-aws-efs`, { names: ["addons.awsEFS"], subLevel: 1 }));
    }
    if (isAddonAWSELBEnabled) {
      links.push(toLinkPage("AWS ELB", `/addons/addon-aws-elb`, { names: ["addons.awsELB"], subLevel: 1 }));
    }
    if (isAddonACREnabled) {
      links.push(toLinkPage("Azure ACR", `/addons/addon-azure-acr`, { names: ["addons.azureACR"], subLevel: 1 }));
    }
    if (isAddonAWIEnabled) {
      links.push(
        toLinkPage("Azure Workload ID", `/addons/addon-azure-workload-identity`, {
          names: ["addons.azureWorkloadIdentity"],
          subLevel: 1,
        }),
      );
    }
    if (isAddonLogsEnabled) {
      links.push(toLinkPage("Logs", `/addons/addon-logs`, { names: ["addons.logs"], subLevel: 1 }));
    }
    if (isAddonMetricsEnabled) {
      links.push(toLinkPage("Metrics", `/addons/addon-metrics`, { names: ["addons.metrics"], subLevel: 1 }));
    }
    if (isAddonNvidiaEnabled) {
      links.push(toLinkPage("NVIDIA", `/addons/addon-nvidia`, { names: ["addons.nvidia"], subLevel: 1 }));
    }
    if (isAddonRegistryMirrorEnabled) {
      links.push(
        toLinkPage("Registry Mirror", `/addons/addon-registry-mirror`, {
          names: ["addons.registryMirror"],
          subLevel: 1,
        }),
      );
    }
    links.push(
      toLinkPage("Firewall", `/firewall`, { names: ["firewall"] }),
      toLinkPage("Tags", `/tags`, { names: ["tags"] }),
      toLinkSpace(),
      toLinkButton("Cancel", {
        type: "primary",
        onClick: () => {
          ignoreBlockerRef.current = true;
          const back = pathname.split(`/-create`)[0];
          dispatch(setRedirect(back));
        },
        disabled: submitting,
        danger: true,
      }),
      toLinkButton("Preview", {
        type: "default",
        onClick: () => formSet(formId, `meta.previewing`, true),
        disabled: submitting,
      }),
      toLinkButton(asyncValidating ? "Validating..." : "Create", {
        type: "primary",
        onMouseDown: async () => {
          await submit();
        },
        loading: submitting,
        disabled: submitting || asyncValidating,
        className: "action",
      }),
    );

    if (IS_LOCAL || !IS_DEPLOYMENT_ENV_PROD) {
      links.push(
        toLinkButton("Debug", {
          type: "default",
          onClick: () => formSet(formId, `meta.debugging`, true),
        }),
      );
    }

    return links;
  }, [
    provider,
    submitting,
    asyncValidating,
    isAddonCplnPlatformEnabled,
    isAddonAWSECREnabled,
    isAddonAWSEFSEnabled,
    isAddonAWSELBEnabled,
    isAddonACREnabled,
    isAddonAWIEnabled,
    isAddonLogsEnabled,
    isAddonMetricsEnabled,
    isAddonNvidiaEnabled,
    isAddonRegistryMirrorEnabled,
  ]);

  return (
    <BasePathContext.Provider value={basePath}>
      <ContentLayout
        nav={<SingleNavMenu useDetailHeader={false} links={links} />}
        content={
          <div className="cpln-cards-container">
            <CpRootError />
            <Routes>
              <Route path={`general/*`} element={<General />} />
              <Route path={`provider-${provider}/*`} element={<Provider />} />
              <Route path={`node-pools-${provider}/*`} element={<NodePools />} />
              {["hetzner"].includes(provider) ? (
                <Route path={`dedicated-server-node-pools-${provider}/*`} element={<UnmanagedNodePools />} />
              ) : null}
              {["lambdalabs", "oblivus", "paperspace"].includes(provider) ? (
                <Route path={`unmanaged-node-pools-${provider}/*`} element={<UnmanagedNodePools />} />
              ) : null}
              <Route path={`addons`} element={<Addons />} />
              <Route path={`addons/addon-cpln-platform/*`} element={<AddonByok />} />
              <Route path={`addons/addon-aws-ecr/*`} element={<AddonAwsEcr />} />
              <Route path={`addons/addon-aws-efs/*`} element={<AddonAwsEfs />} />
              <Route path={`addons/addon-aws-elb/*`} element={<AddonAwsElb />} />
              <Route path={`addons/addon-azure-acr/*`} element={<AddonAzureAcr />} />
              <Route path={`addons/addon-azure-workload-identity/*`} element={<AddonAzureWorkloadIdentity />} />
              <Route path={`addons/addon-logs/*`} element={<AddonLogs />} />
              <Route path={`addons/addon-metrics/*`} element={<AddonMetrics />} />
              <Route path={`addons/addon-nvidia/*`} element={<AddonNvidia />} />
              <Route path={`addons/addon-registry-mirror/*`} element={<AddonRegistryMirror />} />
              <Route path={`firewall/*`} element={<Firewall />} />
              <Route path={`tags/*`} element={<CpTagsNew />} />
            </Routes>
          </div>
        }
      />
    </BasePathContext.Provider>
  );
};
